import { Banner, Box, useSnackbars } from '@customerio/pluma-components/react';
import { useUpdateCollection } from '@/services/db/collection/mutations';
import { type Collection } from '@/services/db/collection/entity';
import { buildClearErrorRequest } from './collection-payload';

type CollectionImportErrorBannerProps = {
  collection: Collection;
  workspaceId: number;
  /** Try again + Dismiss both perform edits, so they only render for editors. */
  canEdit: boolean;
  onTryAgain: () => void;
};

export function CollectionImportErrorBanner({
  collection,
  workspaceId,
  canEdit,
  onTryAgain,
}: CollectionImportErrorBannerProps) {
  const update = useUpdateCollection({ workspaceId });
  const snackbars = useSnackbars();

  if ((collection.import_error?.length ?? 0) === 0) {
    return null;
  }

  const dismiss = async () => {
    try {
      await update.mutateAsync({ id: collection.id, update: buildClearErrorRequest() });
    } catch {
      snackbars.create({ variant: 'error', message: "Couldn't dismiss the error. Try again." });
    }
  };

  // "adding" for a collection that never held data (zero rows) vs "updating" for a
  // re-import — based on rows alone, independent of the in-progress import state.
  const description =
    collection.rows === 0
      ? `Retry adding your collection to use data from ${collection.name} in your messages.`
      : `Retry updating your collection to use new data from ${collection.name} in your messages.`;

  return (
    <Box style={{ gridColumn: '1 / -1' }}>
      <Banner
        variant="error"
        title={`Failed to import your ${collection.name} collection.`}
        description={description}
        // Mirrors the header's Update collection button: same re-import path, same
        // enabled state (editors only, never mid-import).
        action={{ label: 'Try again', onClick: onTryAgain, isDisabled: !canEdit || collection.importing }}
        shouldShowDismissButton={canEdit}
        isDismissButtonDisabled={collection.importing}
        onClose={canEdit ? () => void dismiss() : undefined}
      />
    </Box>
  );
}
